import { create } from "zustand"
import { persist } from "zustand/middleware"
import { AxiosError } from "axios"

import type { AuthState, LoginForm, RegisterForm, LoginResponse, Auth } from "@src/types"

import { getFullPath, removeFromStorage, STORAGE_KEYS } from "@utils"
import { apiService } from "@services/apiService"
import { useLegacyStore } from "@store/legacyStore"

interface AuthStore extends AuthState {
  // Actions
  loginGoogle: (params: URLSearchParams) => Promise<void>
  login: (credentials: LoginForm) => Promise<void>
  loginAdmin: (credentials: LoginForm) => Promise<void>
  register: (userData: RegisterForm) => Promise<BaseErrors | string>
  logout: (ignoreHttp?: boolean) => Promise<void>
  verifyEmail: (token: string) => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (token: string, password: string) => Promise<void>
  updateUser: (userData: Partial<IProfileForm>) => Promise<void>
  updatePassword: (payload: IProfileChangePasswordForm) => Promise<void>
  me: () => Promise<void>
  resendEmail: () => Promise<void>
  setLoading: (loading: boolean) => void
  isAdmin: () => boolean
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      myDataHasBeenExtracted: null,
      loginGoogle: async (params: URLSearchParams) => {
        set({ isLoading: true })
        try {
          const response = await apiService.auth.googleRedirect(params.get("code") as string)
          const { data, status } = response.data as BaseResponse<LoginResponse>
          if (status) {
            const userData: AuthState = {
              myDataHasBeenExtracted: null,
              isAuthenticated: true,
              isLoading: false,
              ...data,
            }
            set(userData)
          }
        } catch (error: any) {
          const _error_ = error.response?.data
          if (_error_.status >= 400) {
            if ("errors" in _error_.data) {
              throw _error_.data || ({} as ValidationError)
            }
            throw _error_.data || ({} as GenericError)
          }
          set({ isLoading: false })
          throw _error_
        }
      },
      login: async (credentials: LoginForm) => {
        set({ isLoading: true })
        try {
          const response = await apiService.auth.login(credentials)
          const { data, status } = response.data as BaseResponse<LoginResponse>
          if (status) {
            const userData: AuthState = {
              myDataHasBeenExtracted: null,
              isAuthenticated: true,
              isLoading: false,
              ...data,
            }
            set(userData)
          }
        } catch (error: any) {
          const _error_ = error.response?.data
          if (_error_.status >= 400) {
            if ("errors" in _error_.data) {
              throw _error_.data || ({} as ValidationError)
            }
            throw _error_.data || ({} as GenericError)
          }
          set({ isLoading: false })
          throw _error_
        }
      },
      loginAdmin: async (credentials: LoginForm) => {
        set({ isLoading: true })
        try {
          const response = await apiService.auth.loginAdmin(credentials)
          const { data, status } = response.data as BaseResponse<LoginResponse>
          if (status) {
            const userData: AuthState = {
              myDataHasBeenExtracted: null,
              isAuthenticated: true,
              isLoading: false,
              ...data,
            }
            set(userData)
          }
        } catch (error: any) {
          const _error_ = error.response?.data
          if (_error_.status >= 400) {
            if ("errors" in _error_.data) {
              throw _error_.data || ({} as ValidationError)
            }
            throw _error_.data || ({} as GenericError)
          }
          set({ isLoading: false })
          throw _error_
        }
      },
      register: async (userData: RegisterForm): Promise<BaseErrors | string> => {
        set({ isLoading: true })
        try {
          const response = await apiService.auth.register(userData)
          const { data, status, msg } = response.data
          if (!status) {
            throw data
          }
          return msg
        } catch (error) {
          if (error instanceof AxiosError) {
            const errResponse = error.response?.data as BaseResponse<BaseErrors>
            throw errResponse.data
          }
          throw error
        } finally {
          set({ isLoading: false })
        }
      },
      logout: async (ignoreHttp: boolean = false): Promise<void> => {
        set({ isLoading: true })
        try {
          if (ignoreHttp) {
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
            })
            useLegacyStore.getState().clearLegacyStore()
            Object.values(STORAGE_KEYS).forEach(removeFromStorage)
          } else {
            const response = await apiService.auth.logout()
            const { status } = response.data as GenericError
            if (status) {
              set({
                user: null,
                token: null,
                isAuthenticated: false,
                isLoading: false,
              })
              useLegacyStore.getState().clearLegacyStore()
              Object.values(STORAGE_KEYS).forEach(removeFromStorage)
              return
            }
            throw response.data
          }
        } catch (error) {
          throw error
        } finally {
          set({ isLoading: false })
        }
      },
      verifyEmail: async (token: string) => {
        set({ isLoading: true })
        try {
          const response = await fetch("/api/auth/verify-email", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ token }),
          })

          if (!response.ok) {
            throw new Error("Email verification failed")
          }

          const data = await response.json()

          set({
            user: data.user,
            isLoading: false,
          })
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },
      forgotPassword: async (email: string) => {
        set({ isLoading: true })
        try {
          const response = await fetch("/api/auth/forgot-password", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ email }),
          })

          if (!response.ok) {
            throw new Error("Password reset request failed")
          }

          set({ isLoading: false })
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },
      resetPassword: async (token: string, password: string) => {
        set({ isLoading: true })
        try {
          const response = await fetch("/api/auth/reset-password", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ token, password }),
          })

          if (!response.ok) {
            throw new Error("Password reset failed")
          }

          set({ isLoading: false })
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },
      updateUser: async (payload: Partial<IProfileForm>) => {
        try {
          const response = await apiService.auth.updateProfile(payload)
          const { data, status } = response.data as BaseResponse<Auth>
          if (!status) {
            throw data
          }
          set({ user: data })
        } catch (error: any) {
          const _error_ = error.response?.data
          if (_error_.status >= 400) {
            if ("errors" in _error_.data) {
              throw _error_.data || ({} as ValidationError)
            }
            throw _error_.data || ({} as GenericError)
          }
          throw _error_
        }
      },
      updatePassword: async (payload: IProfileChangePasswordForm) => {
        set({ isLoading: true })
        try {
          const response = await apiService.auth.updatePassword(payload)
          const { data, status } = response.data as BaseResponse<IProfileChangePasswordForm>
          if (!status) {
            throw data
          }
          set({ isLoading: false })
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },
      resendEmail: async () => {
        // set({ isLoading: true })
        try {
          const response = await apiService.auth.resendEmailVerification()
          const { status } = response.data
          if (!status) {
            throw response.data
          }
          // set({ isLoading: false })
        } catch (error) {
          // set({ isLoading: false })
          throw error
        }
      },
      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },
      me: async () => {
        const { myDataHasBeenExtracted } = get()
        if (myDataHasBeenExtracted === "extracting" || myDataHasBeenExtracted === "extracted") return
        set({ myDataHasBeenExtracted: "extracting" })
        try {
          const response = await apiService.auth.me()
          const { data, status } = response.data as BaseResponse<{ user: Auth }>
          if (!status) {
            throw data
          }
          const newUserData: Auth = {
            ...data.user,
            avatar_path: getFullPath(data.user.avatar_path),
          }
          set({ user: newUserData, myDataHasBeenExtracted: "extracted" })
        } catch (error) {
          set({ myDataHasBeenExtracted: null })
          throw error
        }
      },
      isAdmin: () => {
        const { user } = get()
        return user?.rol === "admin"
      },
    }),
    {
      name: STORAGE_KEYS.AUTH,
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)
